import { authApiClient } from '../auth/AuthApiClient';

const PRESENTER_API_BASE = (
  import.meta.env.VITE_PRESENTER_API_BASE ||
  '/presenter-api'
).replace(/\/$/, '');

export type BillingMode = 'disabled' | 'stripe_test' | 'live';

export interface BillingConfig {
  configured: boolean;
  billing_mode: BillingMode;
}

export interface BillingPlan {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  price_cents: number;
  currency: string;
  interval: 'month' | 'year';
  tier: string;
  active: boolean;
}

export interface BillingEntitlement {
  tier: string;
  features: string[];
  limits: Record<string, number>;
  usage_today: Record<string, number>;
}

export interface BillingSubscription {
  status: string;
  plan: string;
  current_period_end?: string | null;
  cancel_at_period_end: boolean;
}

export interface BillingStatus {
  configured: boolean;
  billing_mode: BillingMode;
  customer_id?: number | null;
  entitlement: BillingEntitlement;
  subscription?: BillingSubscription | null;
}

export interface CheckoutSession {
  session_id: string;
  url: string;
  status: 'redirect';
  billing_mode: BillingMode;
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const payload = await response.json();
      detail = payload.detail || payload.error || detail;
    } catch {
      // Preserve the useful HTTP fallback.
    }
    throw new Error(String(detail));
  }
  return response.json() as Promise<T>;
}

export class BillingApiClient {
  async getConfig(): Promise<BillingConfig> {
    return readJson<BillingConfig>(await fetch(`${PRESENTER_API_BASE}/api/v1/billing/config`));
  }

  async listPlans(): Promise<BillingPlan[]> {
    return readJson<BillingPlan[]>(await fetch(`${PRESENTER_API_BASE}/api/v1/billing/plans`));
  }

  async getStatus(): Promise<BillingStatus> {
    return this.authorized<BillingStatus>('/api/v1/billing/status');
  }

  async createCheckout(planSlug: string): Promise<CheckoutSession> {
    return this.authorized<CheckoutSession>('/api/v1/billing/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_slug: planSlug }),
    });
  }

  async createPortal(): Promise<{ url: string; billing_mode: BillingMode }> {
    return this.authorized('/api/v1/billing/portal', { method: 'POST' });
  }

  private async authorized<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = authApiClient.accessToken;
    if (!token) throw new Error('AUTH_REQUIRED');
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return readJson<T>(await fetch(`${PRESENTER_API_BASE}${path}`, { ...init, headers }));
  }
}

export const billingApiClient = new BillingApiClient();
